import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Рендерер представлений: ответ сервера → экран.
//
// Ни одного нового виджета здесь нет — всё собрано из того, чем уже нарисованы
// обычные разделы: `EntityTable`, `EntityCalendar`, `GovardPropertyList`,
// `Statistic`. Это и есть проверка идеи: если представление нельзя собрать из
// имеющихся кирпичей, значит описание требует того, чего в продукте нет.
//
// Рендерер не знает ни одной сущности поимённо и не ходит в каталог: подписи,
// типы и значения перечислений приходят в самом ответе.
import { X } from 'lucide-react';
import type { ReactElement } from 'react';
import { Alert, Button, Card, Empty, Statistic, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';

import { EntityTable } from '../../../shared/ui/EntityTable.js';
import { GovardPropertyList } from '../../../shared/ui/GovardPropertyList.js';
import { formatMoney } from '../../../shared/format/index.js';
import { ViewPlannerCalendar } from './ViewPlannerCalendar.js';
import type {
  CalendarResult,
  GroupResult,
  RecordResult,
  StatResult,
  TableResult,
  ViewBlockResult,
  ViewResult,
  ViewRow,
} from '../../../views/result.js';
import { ViewCellValue } from './ViewCellValue.js';
import { localizedColumnLabel } from '../../../views/localization.js';

const { Title } = Typography;

function TableBlock({ block }: { block: TableResult }): ReactElement {
  useI18nSubscription();
  const columns: ColumnsType<ViewRow> = block.columns.map((column) => ({
    key: column.key,
    title: localizedColumnLabel(block.entity, column, i18n.locale),
    dataIndex: ['cells', column.key],
    render: (_: unknown, row: ViewRow) => (
      <ViewCellValue entity={block.entity} column={column} value={row.cells[column.key] ?? null} />
    ),
  }));

  return (
    <Card title={block.title} size="small">
      {block.rows.length === 0 ? (
        <Empty
          description={i18n.t(
            'crm.features.features.views.components.viewrenderer.ничего_не_найдено',
          )}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <EntityTable<ViewRow> dataSource={block.rows} columns={columns} paginated={false} />
      )}
    </Card>
  );
}

function StatBlock({ block }: { block: StatResult }): ReactElement {
  return (
    <Card size="small">
      <Statistic
        title={block.title}
        value={block.valueType === 'money' ? formatMoney(block.value) : block.value}
      />
    </Card>
  );
}

function RecordBlock({ block }: { block: RecordResult }): ReactElement {
  return (
    <Card
      size="small"
      title={block.href === null ? block.title : <Link to={block.href}>{block.title}</Link>}
    >
      <GovardPropertyList
        layout="horizontal"
        items={block.fields.map((field) => ({
          key: field.key,
          label: localizedColumnLabel(block.entity, field, i18n.locale),
          value: <ViewCellValue entity={block.entity} column={field} value={field.value} />,
        }))}
      />
    </Card>
  );
}

function CalendarBlock({ block }: { block: CalendarResult }): ReactElement {
  // Календарь представления — тот же WeekPlanner (D «Warm Precision»), что и
  // /calendar: hairline-сетка, бледные чипы, спроектированный dark. Данные
  // приходят строками представления — их в PlannerEvent маппит адаптер.
  return (
    <Card title={block.title} size="small">
      <ViewPlannerCalendar block={block} />
    </Card>
  );
}

function GroupBlock({ block }: { block: GroupResult }): ReactElement {
  useI18nSubscription();
  // Разрез — та же таблица из двух колонок. Отдельный виджет ему не нужен:
  // ценность здесь в числах, а не в оформлении.
  const rows = block.buckets.map((bucket, index) => ({
    id: String(index),
    cells: { key: bucket.key, value: bucket.value },
  }));

  const columns: ColumnsType<(typeof rows)[number]> = [
    {
      key: 'key',
      title: localizedColumnLabel(block.entity, block.by, i18n.locale),
      render: (_: unknown, row) => (
        <ViewCellValue entity={block.entity} column={block.by} value={row.cells.key} />
      ),
    },
    {
      key: 'value',
      title:
        block.agg === 'count'
          ? i18n.t('crm.features.features.views.components.viewrenderer.количество')
          : block.agg === 'sum'
            ? i18n.t('crm.features.features.views.components.viewrenderer.сумма')
            : i18n.t('crm.features.features.views.components.viewrenderer.среднее'),
      align: 'right',
      render: (_: unknown, row) =>
        block.valueType === 'money' ? formatMoney(row.cells.value) : row.cells.value,
    },
  ];

  return (
    <Card title={block.title} size="small">
      {rows.length === 0 ? (
        <Empty
          description={i18n.t(
            'crm.features.features.views.components.viewrenderer.ничего_не_найдено_2',
          )}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <>
          <EntityTable dataSource={rows} columns={columns} paginated={false} />
          {/* Разрез посчитан по части записей — говорим это прямо, и не только про
              сумму: строки за потолком могли переставить корзины местами, значит
              недостоверен и порядок. Молча усечённое число выглядит точным. */}
          {block.truncated === undefined ? null : (
            <Alert
              className="mt-2"
              type="warning"
              showIcon
              title={i18n.t('crm.features.dynamic.features.views.components.viewrenderer.81', {
                p0: block.truncated.scanned,
                p1: block.truncated.total,
              })}
              description={i18n.t(
                'crm.features.features.views.components.viewrenderer.записей_больше_чем_разрез_успевает_пройти_',
              )}
            />
          )}
        </>
      )}
    </Card>
  );
}

export function ViewBlockView({ block }: { block: ViewBlockResult }): ReactElement {
  useI18nSubscription();
  switch (block.kind) {
    case 'table':
      return <TableBlock block={block} />;
    case 'stat':
      return <StatBlock block={block} />;
    case 'record':
      return <RecordBlock block={block} />;
    case 'calendar':
      return <CalendarBlock block={block} />;
    case 'group':
      return <GroupBlock block={block} />;
    default: {
      // Неизвестный вид блока — фолбэк, а не исключение: сохранённое
      // представление живёт дольше бандла, и сообщение из будущей версии
      // сервера не должно ронять всю страницу с историей чата.
      const never: never = block;
      const unknown = never as { kind?: string };
      return (
        <Card size="small">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={i18n.t('crm.features.dynamic.features.views.components.viewrenderer.82', {
              p0: String(unknown.kind ?? '?'),
            })}
          />
        </Card>
      );
    }
  }
}

export function ViewRenderer({
  result,
  showTitle = true,
  onRemoveBlock,
  getRemoveBlockAriaLabel,
  removingBlockIndex,
  disableBlockRemoval = false,
}: {
  result: ViewResult;
  showTitle?: boolean;
  /** Optional editing affordance for a persisted compound widget. */
  onRemoveBlock?: (index: number) => void;
  getRemoveBlockAriaLabel?: (block: ViewBlockResult, index: number) => string;
  removingBlockIndex?: number | undefined;
  disableBlockRemoval?: boolean;
}): ReactElement {
  return (
    <div className="flex flex-col gap-4">
      {showTitle ? (
        <Title level={4} style={{ margin: 0 }}>
          {result.title}
        </Title>
      ) : null}
      {result.blocks.map((block, index) => (
        <div className="relative" key={`${block.kind}-${index}`}>
          <ViewBlockView block={block} />
          {onRemoveBlock === undefined ? null : (
            <Button
              aria-label={getRemoveBlockAriaLabel?.(block, index) ?? `Remove ${block.title}`}
              className="absolute right-2 top-2 z-10"
              danger
              disabled={disableBlockRemoval && removingBlockIndex !== index}
              icon={<X size={14} />}
              loading={removingBlockIndex === index}
              onClick={() => onRemoveBlock(index)}
              size="small"
              type="text"
            />
          )}
        </div>
      ))}
    </div>
  );
}
